import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import NotificationsContext from 'providers/Notifications/Notifications.context';
import {
  getHasElementsInArray,
  getIsNotificationForLeads,
  sortOptions,
} from 'utils';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import LeadNotificationsContext from 'providers/LeadNotifications/LeadNotifications.context';
import { NOTIFICATION_MEDIUM } from 'models/Notification';
import { Button } from 'ncoded-component-library';
import { LEAD_NOTIFICATION_STATUS } from 'models/LeadNotifications';
import SearchableMultiSelectFilter from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/PortfolioSellerFilters/componentes/SearchableMultiSelectFilter';
import api from 'api';
import { Option } from 'models/Global';
import useMatchMedia from 'hooks/useMatchMedia';

import './NotificationsForLeadsFilters.styles.scss';

export const LEAD_NOTIFICATION_STATUS_COPY = {
  [LEAD_NOTIFICATION_STATUS.RECEIVED]: 'delivered',
  [LEAD_NOTIFICATION_STATUS.OPENED]: 'opened',
  [LEAD_NOTIFICATION_STATUS.FAILED]: 'failed',
} as const;

type NotificationsForLeadsFiltersProps = {
  className?: string;
};

const NotificationsForLeadsFilters: React.FC<
  NotificationsForLeadsFiltersProps
> = (props) => {
  const { className } = props;

  const { notificationBy } = useContext(NotificationsContext);
  const {
    channel,
    status,
    leads,
    deals,
    setChannel,
    setStatus,
    setLeads,
    setDeals,
    resetFilters,
  } = useContext(LeadNotificationsContext);

  const classes = classNames('notifications-for-leads-filters', className);

  const isPhablet = useMatchMedia('isPhablet');

  const isForLeads = getIsNotificationForLeads(notificationBy);

  const showClearAll = useMemo(
    () =>
      getHasElementsInArray(channel) ||
      getHasElementsInArray(status) ||
      getHasElementsInArray(leads as Option[]) ||
      getHasElementsInArray(deals as Option[]),
    [channel, deals, leads, status],
  );

  const channelOptions = useCreateSelectOptions()(
    Object.values(NOTIFICATION_MEDIUM),
  );

  const { t } = useTranslation();

  const statusOptions = useMemo(
    () =>
      Object.values(LEAD_NOTIFICATION_STATUS).map((value) => ({
        value,
        label: t(LEAD_NOTIFICATION_STATUS_COPY[value]),
      })),
    [t],
  );

  const getLeadsOptions = useCallback(async (searchString: string) => {
    const { data } = await api.leadNotifications.getLeads({
      $searchString: searchString,
    });

    const options = data.map(({ buyerId, name }) => ({
      value: buyerId,
      label: name,
    }));

    return sortOptions(options);
  }, []);

  const getDealsOptions = useCallback(async (searchString: string) => {
    const { data } = await api.leadNotifications.getDeals({
      $searchString: searchString,
    });

    const options = data.map(({ dealRoomId, name }) => ({
      value: dealRoomId,
      label: name,
    }));

    return sortOptions(options);
  }, []);

  if (!isForLeads) return null;

  return (
    <div className={classes}>
      <ResolveMultiSelect
        hasCheckbox
        searchable={false}
        renderAsPortal={!isPhablet}
        value={channel}
        options={channelOptions}
        pillText={t('type')}
        onChange={setChannel}
      />

      <ResolveMultiSelect
        hasCheckbox
        searchable={false}
        renderAsPortal={!isPhablet}
        value={status}
        options={statusOptions}
        pillText={t('status')}
        onChange={setStatus}
      />

      <SearchableMultiSelectFilter
        value={leads}
        pillText={t('leads')}
        onSelect={setLeads}
        getOptionsOnSearch={getLeadsOptions}
      />

      <SearchableMultiSelectFilter
        value={deals}
        pillText={t('deals')}
        onSelect={setDeals}
        getOptionsOnSearch={getDealsOptions}
      />

      {showClearAll && (
        <Button variant="link" onClick={resetFilters}>
          {t('clearAll')}
        </Button>
      )}
    </div>
  );
};

export default NotificationsForLeadsFilters;
